/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { AlertColor } from "@mui/material";
import { AppContext } from "../../../utils/AppContext";
import { ToolboxBuildsTable } from "./ToolboxBuildsTable";
import { ToolboxBuildForm, ToolboxBuildFormData, ToolboxBuildFormType } from "./ToolboxBuildForm";
import { IToolboxBuild, ToolboxBuildArch, useToolboxBuildService } from "../../../services/registry/ToolboxBuildService";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import { Notify } from "../../../components/Notify";
import { useSearchParams } from "react-router-dom";

const ToolboxBuilds = () => {
  const toolboxBuildService = useToolboxBuildService();

  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [toolboxBuilds, setToolboxBuilds] = useState<IToolboxBuild[]>([]);
  const [selectedItem, setSelectedItem] = useState<IToolboxBuild | undefined>(undefined);
  const [toolboxBuildFormVisible, setToolboxBuildFormVisible] = useState(false);
  const [toolboxBuildFormType, setToolboxBuildFormType] = useState<ToolboxBuildFormType>("new");
  const [deleteToolboxBuildPopupVisible, setDeleteToolboxBuildPopupVisible] = useState<boolean>(false);
  const [initialFilterQuery, setInitialFilterQuery] = useState<string>("");

  useEffect(() => {
    (async () => fetchToolboxBuilds())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchToolboxBuilds = async () => {
    try {
      setLoading(true);
      const { list } = await toolboxBuildService.listToolboxBuilds();
      setToolboxBuilds(list);
      setLoading(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to get Toolbox Builds.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const refreshButtonClickHandler = async () => {
    await fetchToolboxBuilds();
  };

  const onToolboxBuildUploadButtonClick = () => {
    setToolboxBuildFormType("new");
    setToolboxBuildFormVisible(true);
  };

  const onToolboxBuildEditButtonClick = () => {
    setToolboxBuildFormType("edit");
    setToolboxBuildFormVisible(true);
  };

  const toolboxBuildFormAcceptHandler = async (data: ToolboxBuildFormData) => {
    if (toolboxBuildFormType === "new") {
      try {
        if (!data.file || !data.arch) {
          setAlertMessage("Missing file or architecture");
          setAlertSeverity("error");
          return;
        }

        setLoading(true);
        await toolboxBuildService.uploadToolboxBuild(
          data.name,
          data.version,
          data.branch,
          data.arch,
          data.file,
          data.commitHash,
        );
        setToolboxBuildFormVisible(false);
        await fetchToolboxBuilds();
        setAlertMessage("Toolbox build uploaded successfully");
        setAlertSeverity("success");
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not upload the new toolbox build.");
        setAlertSeverity("error");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        await toolboxBuildService.editToolboxBuild(data.id, data.name, data.version, data.branch);
        setToolboxBuildFormVisible(false);
        setSelectedItem({
          ...data,
          creationDate: selectedItem?.creationDate ?? 0,
          arch: selectedItem?.arch ?? ToolboxBuildArch.X86_64,
          commitHash: selectedItem?.commitHash,
        });
        await fetchToolboxBuilds();
        setAlertMessage("Toolbox build updated successfully");
        setAlertSeverity("success");
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not edit the toolbox build.");
        setAlertSeverity("error");
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteToolboxBuildPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      setDeleteToolboxBuildPopupVisible(false);
      await toolboxBuildService.removeToolboxBuild(selectedItem.id);
      setSelectedItem(undefined);
      await fetchToolboxBuilds();
      setAlertMessage("Toolbox build removed successfully");
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not remove the selected Toolbox Build.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const context = React.useContext(AppContext);
  useEffect(() => {
    context?.setTitle("Toolbox Builds");
  }, [context]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && toolboxBuilds) {
      const item = toolboxBuilds.find((t) => t.id == id);
      if (item) {
        setInitialFilterQuery(id);
      }
    }
  }, [searchParams, toolboxBuilds]);

  return (
    <>
      <ToolboxBuildsTable
        rows={toolboxBuilds}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onUploadButtonClick={onToolboxBuildUploadButtonClick}
        onEditButtonClick={onToolboxBuildEditButtonClick}
        onRemoveButtonClick={() => setDeleteToolboxBuildPopupVisible(true)}
        initialFilterQuery={initialFilterQuery}
        onRefreshButtonClick={refreshButtonClickHandler}
      />
      <ToolboxBuildForm
        visible={toolboxBuildFormVisible}
        type={toolboxBuildFormType}
        initialData={toolboxBuildFormType === "edit" ? selectedItem : undefined}
        onDismiss={() => setToolboxBuildFormVisible(false)}
        onAccept={toolboxBuildFormAcceptHandler}
      />
      <YesNoCancelPopup
        visible={deleteToolboxBuildPopupVisible}
        title={"Remove Toolbox Build"}
        message={`Are you sure you want to remove the Toolbox Build named <b>${selectedItem?.name}</b>?<br/>
        This Toolbox Build will become permanently unavailable if you remove it.`}
        onNo={() => setDeleteToolboxBuildPopupVisible(false)}
        onYes={deleteToolboxBuildPopupYesHandler}
      />
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default ToolboxBuilds;
